import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, MapPin, User } from "lucide-react";
import { endpoints } from "@/lib/api";
import { MatchRow, TeamLogo, FavStar } from "@/components/football/MatchRow";

const Squad = ({ squad }) => {
  const groups = squad.reduce((acc, p) => { (acc[p.position] = acc[p.position] || []).push(p); return acc; }, {});
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="team-squad">
      {Object.entries(groups).map(([pos, arr]) => (
        <div key={pos} className="rounded-xl border border-[#273042] bg-[#121620] p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">{pos}s</div>
          {arr.map((p) => (
            <Link key={p.id} to={`/football/player/${p.id}`} className="flex items-center gap-2 py-1.5 text-sm text-slate-200 hover:text-white" data-testid={`squad-player-${p.id}`}>
              <span className="w-6 text-right text-slate-500 tabular-nums">{p.number}</span>
              {p.photoUrl ? <img src={p.photoUrl} alt="" className="w-7 h-7 rounded-full object-cover bg-[#0A0C10]" loading="lazy" /> : <span className="w-7 h-7 rounded-full bg-[#0A0C10]" />}
              <span className="truncate">{p.name}</span>{p.captain && <span className="text-[10px] font-bold text-amber-400">C</span>}
            </Link>
          ))}
        </div>
      ))}
    </div>
  );
};

export default function FootballTeam() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("results");
  useEffect(() => { setT(null); endpoints.footballTeam(id).then((r) => setT(r.data)).catch(() => setErr(true)); }, [id]);
  if (err) return <div className="text-center text-slate-300 py-24" data-testid="team-error">Team details are unavailable.</div>;
  if (!t) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#E11D2A]" /></div>;
  const tabs = [["results", `Results (${t.latest.length})`], ["fixtures", `Fixtures (${t.upcoming.length})`], ["squad", `Squad (${t.squad.length})`]];
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="team-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-5"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-5 mb-6">
        <TeamLogo team={t} size="w-20 h-20" />
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white flex items-center gap-2">{t.name}<FavStar kind="teams" testid="fav-team" item={{ id: t.id ?? id, name: t.name, logoUrl: t.logoUrl, country: t.country }} /></h1>
          <div className="text-sm text-slate-300 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {t.country && <span>{t.country}</span>}{t.founded && <span>Founded {t.founded}</span>}
            {t.venue?.name && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{t.venue.name}{t.venue.capacity ? ` (${t.venue.capacity.toLocaleString()})` : ""}</span>}
            {t.coach?.name && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />Coach: {t.coach.name}</span>}
          </div>
        </div>
      </div>
      <div className="flex gap-1 border-b border-[#273042] mb-5 overflow-x-auto">
        {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`team-tab-${k}`}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === k ? "text-white border-[#E11D2A]" : "text-slate-400 border-transparent hover:text-white"}`}>{l}</button>)}
      </div>
      {tab === "squad" ? <Squad squad={t.squad} /> : (
        <div className="space-y-2" data-testid={`team-${tab}`}>
          {(tab === "results" ? t.latest : t.upcoming).map((f) => <MatchRow key={f.id} f={f} showDate showLeague />)}
          {(tab === "results" ? t.latest : t.upcoming).length === 0 && <p className="text-slate-400 text-center py-10">Nothing to show.</p>}
        </div>
      )}
    </div>
  );
}
